import type { DocItem } from 'src/@types/docs';
import type { TreeItem } from './types';

export const sortByCreationDate = (a: TreeItem, b: TreeItem): number => {
  if (a.data?.order === -1 || b.data?.order === -1) {
    return 1;
  }
  return (
    new Date(b?.data?.createdAt ?? 0).getTime() -
    new Date(a?.data?.createdAt ?? 0).getTime()
  );
};

export const sortByOrder = (a: TreeItem, b: TreeItem): number => {
  const ao = a.data?.order ?? 2;
  const bo = b.data?.order ?? 2;
  return ao - bo;
};

export const useNavFilteredTreeItems = (treeItems: TreeItem[]): TreeItem[] => {
  const rootItems = treeItems.filter((item) => item.parent === 'root');

  const allowedParentIds = [...rootItems]
    .sort(sortByCreationDate)
    .slice(0, 8)
    .map((item) => item.id);

  const allowedRootItems = treeItems.filter(
    (item) => item.parent === 'root' && allowedParentIds.includes(item.id),
  );

  const getRecursiveItems = (
    parentIds: string[],
    items: TreeItem[],
  ): TreeItem[] => {
    const children = items.filter((item) =>
      parentIds.includes(String(item.parent)),
    );
    if (!children.length) return [];
    const childIds = children.map((child) => String(child.id));
    return [...children, ...getRecursiveItems(childIds, items)];
  };

  const recursiveItems = getRecursiveItems(
    allowedRootItems.map((item) => String(item.id)),
    treeItems,
  );

  return [...allowedRootItems, ...recursiveItems];
};

export function checkDocPermission(doc: DocItem, userId?: string): boolean {
  if (!userId) {
    return false;
  }

  const viewerRoles = doc?.roles?.viewer;
  const isViewer = viewerRoles?.[String(userId)] === true;
  const isOwner = doc.owner === userId;

  return !isViewer && isOwner;
}

export function getAllParentIds(
  dataArray: TreeItem[],
  startingId: string,
): string[] {
  const parentIds: any[] = [];

  function traverseParents(currentId: any) {
    const node = dataArray.find((item) => item.id === currentId);

    if (node) {
      if (node.parent !== 'root' && node.parent !== null) {
        parentIds.push(node.parent);
        traverseParents(node.parent);
      }
    }
  }

  traverseParents(startingId);
  return parentIds;
}
